/**
 * S19 — client-side role gate for the /admin/* subtree.
 *
 * Defense-in-depth only: the hub's per-route `requireAdmin` 403s protect
 * the data. This gate keeps non-admin (but authenticated) users out of the
 * admin chrome by redirecting them to the hub root subtree.
 *
 * The render decision is split into the pure `renderGate` so it can be
 * tested with `renderToStaticMarkup` (admin test convention — no jsdom,
 * no testing-library) without booting a refine context.
 */
import type { ReactNode } from 'react';
import { usePermissions } from '@refinedev/core';
import { Navigate } from 'react-router';

/**
 * Fail-closed admin check: only a real array containing the string
 * 'admin' (SYSTEM_ADMIN_ROLE) passes. undefined / null / non-array
 * inputs are all rejected.
 */
export function isAdminRoles(roles: unknown): boolean {
  return Array.isArray(roles) && roles.includes('admin');
}

export interface GateState {
  isLoading: boolean;
  roles: unknown;
}

/**
 * Pure gate decision: loading → null (render nothing while the roles
 * query is in flight); admin → children; anything else → redirect to
 * the hub root subtree.
 */
export function renderGate(state: GateState, children: ReactNode): ReactNode {
  if (state.isLoading) return null;
  if (isAdminRoles(state.roles)) return children;
  return <Navigate to="/" replace />;
}

/**
 * Role gate for use INSIDE `<Authenticated key="admin">` (auth first,
 * then role). Roles come from `authProvider.getPermissions()` via
 * refine's `usePermissions()` (data = `me.user.roles`).
 */
export function RequireAdmin({ children }: { children: ReactNode }): ReactNode {
  const { data, isLoading } = usePermissions<unknown>({});
  return renderGate({ isLoading, roles: data }, children);
}
